// DS: C119A
import React, { useMemo, Fragment } from 'react';
import classnames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import ChatThumbnail from '../ChatThumbnail';
import VisuallyHidden from '../../a11y/VisuallyHidden';
import PlainButtonOrLink from '../../Button/PlainButtonOrLink';
import { ChatParticipantDisplayDataTypes, } from '../redux/types';
import get from 'lodash/get';
import ChatUserLink from '../ChatUserLink';
import UserAvatarStack from '../UserAvatarStack';
import { BiEventType } from '../../biLogger';
import Timestamp from '../../Timestamp';
import { getMultiChatStackInfo, toDisplayData } from './helpers';
import ChatActionsIconWithDropdown from '../ChatActionsIconWithDropdown';
import s from './ChatSummaryItem.scss';
export var ChatSummaryItem = function (_a) {
    var chatroom = _a.chatroom, className = _a.className, avatarClassName = _a.avatarClassName, onClick = _a.onClick, isMuted = _a.isMuted, ariaLabel = _a["aria-label"], _b = _a.tag, Tag = _b === void 0 ? 'section' : _b, currentUser = _a.currentUser;
    var t = useTranslation().t;
    var _c = useMemo(function () { return getChatInfoFromChatroom(t, chatroom, currentUser); }, [chatroom, currentUser, t]), thumbnail = _c.thumbnail, name = _c.name, lastUpdate = _c.lastUpdate, summary = _c.summary, unreadCount = _c.unreadCount;
    if (!chatroom) {
        return React.createElement("div", null, "Failed to render chatroom");
    }
    var displayUnread = !isMuted && unreadCount !== undefined && unreadCount > 0;
    var timestamp = (parseInt(lastUpdate) / 1000).toFixed(0);
    return (React.createElement(Tag, { className: classnames(s['root'], className) },
        React.createElement("div", { className: s['controls'] },
            React.createElement(ChatActionsIconWithDropdown, { chatroom: chatroom, className: s['toggle'] })),
        React.createElement(PlainButtonOrLink, { className: s['button'], onClick: onClick, "aria-label": ariaLabel, biData: {
                evid: BiEventType.CHAT_SELECT,
                chat_uuid: chatroom.id,
            } }),
        React.createElement("div", { className: s['positioning-wrapper'] },
            Array.isArray(thumbnail) && React.createElement(UserAvatarStack, { users: thumbnail }),
            !Array.isArray(thumbnail) && (React.createElement(ChatThumbnail, { src: thumbnail, avatarClassName: avatarClassName })),
            React.createElement("div", { className: s['details'] },
                React.createElement("div", { className: s['chat-name-and-timestamp-block'] },
                    React.createElement("h3", { className: classnames(s['chat-name'], !chatroom.isPrivate && s['multi-chat']) }, name),
                    React.createElement("p", { className: s['timestamp'] },
                        React.createElement(Timestamp, { timestamp: "" + timestamp }))),
                React.createElement("div", { className: s['summary-and-indicator-block'] },
                    React.createElement("p", { className: s['summary'] }, summary),
                    displayUnread && (React.createElement("div", { className: s['new-message-indicator'] },
                        React.createElement(Trans, { values: { count: unreadCount }, i18nKey: "chat.summary.newMessages" },
                            React.createElement(VisuallyHidden, null, "new messages")))))))));
};
ChatSummaryItem.displayName = 'ChatSummaryItem';
function buildChatSummary(t, chatroom, currentUser) {
    var sender = get(chatroom, 'lastMessage.sender');
    var summaryMsg = get(chatroom, 'lastMessage.summary');
    var isPrivate = get(chatroom, 'isPrivate', false);
    var isCurrentUser = sender.userId === currentUser.useridUuid;
    var summarySender = isCurrentUser
        ? {
            id: sender.userId,
            displayData: {
                name: t('chat.summary.you'),
                image: {
                    url: currentUser.usericon,
                },
                // To make sure no user symbol gets added
                initials: '',
            },
        }
        : toDisplayData(get(chatroom, 'lastMessage.senderDisplayData'), {
            includeUserSymbol: false,
        });
    var shouldRenderSenderName = (isPrivate && isCurrentUser && summarySender) ||
        (!isPrivate && summarySender);
    return (React.createElement(React.Fragment, null,
        shouldRenderSenderName && (React.createElement(React.Fragment, null,
            React.createElement(ChatUserLink, { participant: summarySender, noLink: true }),
            ":",
            ' ')),
        summaryMsg));
}
function getChatInfoFromChatroom(t, chatroom, currentUser) {
    if (!chatroom) {
        return {
            thumbnail: '',
            name: '???',
            lastUpdate: '',
            summary: '???',
            unreadCount: 0,
        };
    }
    // Wix has different set of types
    var chatType = chatroom.siteUserType;
    var unreadCount = get(chatroom, 'count', 0);
    var lastUpdate = get(chatroom, 'lastMessage.createdAt', get(chatroom, 'createdAt'));
    // Chat has been renamed
    if (chatType === ChatParticipantDisplayDataTypes.ROOM) {
        return {
            thumbnail: get(chatroom, 'lastMessage.senderDisplayData.image.url', ''),
            name: chatroom.name,
            lastUpdate: lastUpdate,
            summary: buildChatSummary(t, chatroom, currentUser),
            unreadCount: unreadCount,
        };
    }
    // Multi chat
    if (!chatroom.isPrivate) {
        var infoStack_1 = getMultiChatStackInfo(chatroom, currentUser);
        // Build usernames format of: {username}, {username}
        var name_1 = '';
        if (infoStack_1.length > 0) {
            /* prettier-ignore */
            name_1 = (React.createElement(React.Fragment, null,
                React.createElement("span", { className: s['name-left'] }, infoStack_1.map(function (participant, idx) { return (React.createElement(Fragment, { key: chatroom.id + "-" + participant.displayData.name + "-info-link" },
                    React.createElement(ChatUserLink, { participant: participant, noLink: true }),
                    idx !== infoStack_1.length - 1 && React.createElement(React.Fragment, null, ",\u00A0"))); }))));
        }
        else {
            /* prettier-ignore */
            name_1 = (React.createElement(Trans, { values: { count: chatroom.participantCount - 1 }, i18nKey: "chat.summary.numberOthersFallback" }, "Conversation with N others"));
        }
        return {
            thumbnail: infoStack_1,
            name: name_1,
            lastUpdate: lastUpdate,
            summary: buildChatSummary(t, chatroom, currentUser),
            unreadCount: unreadCount,
        };
    }
    // DM case
    return {
        thumbnail: chatroom.thumbnail || '',
        name: (React.createElement(ChatUserLink, { noLink: true, participant: toDisplayData({
                name: chatroom.name,
                image: { url: chatroom.thumbnail || '' },
            }, { includeUserSymbol: false }) })),
        lastUpdate: lastUpdate,
        summary: buildChatSummary(t, chatroom, currentUser),
        unreadCount: unreadCount,
    };
}
export default ChatSummaryItem;
//# sourceMappingURL=ChatSummaryItem.js.map